import React, { useEffect, useState } from 'react';
import {
  Box,
  FormControl,
  FormLabel,
  Select,
  Text,
  useToast,
  Spinner,
  Flex,
  Badge,
  Tooltip,
  Icon,
  HStack
} from '@chakra-ui/react';
import { FiInfo } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import { fetchApi } from '../../../lib/apiClient';
import { useAuth } from '../../../lib/auth/AuthProvider';
import { trackEvent } from '../../../lib/analytics';

interface ModelOption {
  name: string;
  description: string;
}

export const ModelPreferenceSelector = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [models, setModels] = useState<ModelOption[]>([]);
  const [selectedModel, setSelectedModel] = useState<string>('');
  const toast = useToast();
  const { t } = useTranslation(['v0/me', 'v0/common']);

  // 获取可用模型和用户当前偏好
  useEffect(() => {
    const fetchModelPreferences = async () => {
      try {
        setLoading(true);
        const response = await fetchApi('/profiles/me/preferences');
        setModels(response.availableModels || []);
        setSelectedModel(response.preferredClaudeModel || '');
      } catch (error) {
        console.error('Failed to fetch model preferences:', error);
        toast({
          title: t('v0/common:errors.loadFailed'),
          description: t('v0/me:modelPreferences.loadError'),
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchModelPreferences();
  }, [toast, t]);

  // 保存用户模型偏好
  const handleModelChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newModel = e.target.value;
    const oldModel = selectedModel;

    try {
      setSaving(true);

      // 记录模型选择事件
      await trackEvent('model selected', {
        model: newModel,
        previous_model: oldModel || 'none'
      });

      await fetchApi('/profiles/me/preferences', {
        method: 'PUT',
        body: { preferredClaudeModel: newModel }
      });

      setSelectedModel(newModel);

      // 记录模型保存成功事件
      await trackEvent('model preference saved', {
        model: newModel,
        previous_model: oldModel || 'none'
      });

      toast({
        title: t('v0/me:modelPreferences.saveSuccess'),
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Failed to save model preference:', error);

      // 记录模型保存失败事件
      await trackEvent('model preference save failed', {
        model: newModel,
        previous_model: oldModel || 'none',
        error: error instanceof Error ? error.message : String(error)
      });

      toast({
        title: t('v0/common:errors.saveFailed'),
        description: t('v0/me:modelPreferences.saveError'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  // 获取模型类型标签
  const getModelTypeBadge = (modelName: string) => {
    if (modelName.includes('haiku')) {
      return (
        <Badge colorScheme="green" fontSize="xs">
          {t('v0/me:modelPreferences.fast')}
        </Badge>
      );
    } else if (modelName.includes('sonnet')) {
      return (
        <Badge colorScheme="blue" fontSize="xs">
          {t('v0/me:modelPreferences.balanced')}
        </Badge>
      );
    } else if (modelName.includes('opus')) {
      return (
        <Badge colorScheme="purple" fontSize="xs">
          {t('v0/me:modelPreferences.powerful')}
        </Badge>
      );
    }
    return null;
  };

  return (
    <Box>
      <FormControl>
        <FormLabel fontWeight="medium" display="flex" alignItems="center">
          {t('v0/me:modelPreferences.title')}
          <Tooltip label={t('v0/me:modelPreferences.tooltip')}>
            <Icon as={FiInfo} ml={1} color="gray.500" />
          </Tooltip>
        </FormLabel>

        {loading ? (
          <Flex justify="center" align="center" py={4}>
            <Spinner size="md" />
          </Flex>
        ) : (
          <Select
            value={selectedModel}
            onChange={handleModelChange}
            isDisabled={saving}
            placeholder={t('v0/me:modelPreferences.selectModel')}
          >
            {models.map((model) => (
              <option key={model.name} value={model.name}>
                {model.description}
              </option>
            ))}
          </Select>
        )}

        {selectedModel && !loading && (
          <HStack mt={2} spacing={2}>
            {getModelTypeBadge(selectedModel)}
            {saving && <Spinner size="xs" />}
          </HStack>
        )}

        <Text fontSize="xs" color="gray.500" mt={1}>
          {t('v0/me:modelPreferences.description')}
        </Text>
      </FormControl>
    </Box>
  );
};
